// Component-free leaf module holding ONLY the mandate-form constants that the pure-logic
// validators in Components/Sections/*Types.ts (businessDetailsTypes.ts, coApplicantTypes.ts)
// import. They live here rather than in DealDrawerConstants.tsx — which imports the whole
// component tree — so those validators (and their colocated unit tests) can import them WITHOUT
// transiting the component tree. Importing them via DealDrawerConstants closed a circular import
// (DealDrawerConstants -> CompanyLegalNameSearch -> ... -> useManageDealDrawer -> coApplicantTypes
// -> DealDrawerConstants) that threw a temporal-dead-zone ReferenceError when a *Types.ts module
// was the entry point (e.g. businessDetailsTypes.test). DealDrawerConstants re-exports everything
// here, so its importers are unchanged. Every other applicant/business-details constant (labels,
// options, picker/loan-purpose copy) stays in DealDrawerConstants — only what the validators need
// is here.

// Field keys shared by the primary Applicant block and every Co-applicant row (both reuse the
// same `primaryApplicant` section config from AICA_COMPANY_PROFILE). Must match the godzilla
// config, the Deal doc, and the completeness reader byte-for-byte.
export const MANDATE_APPLICANT_FIELD_KEYS = {
    NAME: 'name',
    PAN: 'pan',
    MOTHER_NAME: 'motherName',
    DOB: 'dob',
    PHONE: 'phone',
    EMAIL: 'email',
    CURRENT_ADDRESS: 'currentAddress',
    CURRENT_ADDRESS_OWNERSHIP: 'currentAddressOwnership',
    PERMANENT_ADDRESS: 'permanentAddress',
    PERMANENT_ADDRESS_OWNERSHIP: 'permanentAddressOwnership',
    PERMANENT_SAME_AS_CURRENT: 'permanentSameAsCurrent',
    CURRENT_ADDRESS_PINCODE: 'currentAddressPincode',
} as const;

export const MANDATE_APPLICANT_PHONE_DIGIT_LENGTH = 10;

// Shared by both pincode inputs (Applicant Details' currentAddressPincode and Business Details'
// businessAddressPincode) - a pincode is always 6 numeric digits.
export const PINCODE_DIGIT_LENGTH = 6;

// Card-header dropdown text - the applicant's role for this mandate, an explicit per-card choice
// (see MANDATE_APPLICANT_TYPE_KEY in DealDrawerConstants), never derived from the card's position.
export const MANDATE_APPLICANT_DESIGNATION_LABELS = {
    PRIMARY: 'Primary Applicant',
    CO_APPLICANT_LABEL: 'Co-Applicant',
};

// UI-state-only field key (not in MANDATE_APPLICANT_FIELD_KEYS - never persisted on its own).
// The card-header "Primary Applicant" / "Co-Applicant" dropdown; the picked value drives both the
// designation sent to the backend and which bucket (primaryApplicant vs coApplicants[]) the card
// saves into - see coApplicantTypes.splitApplicantCardsByType.
export const MANDATE_APPLICANT_TYPE_KEY = 'applicantType';

export const MANDATE_APPLICANT_PRIMARY_MISSING_ERROR = 'Mark one applicant as the Primary Applicant.';
export const MANDATE_APPLICANT_PRIMARY_DUPLICATE_ERROR = 'Only one applicant can be the Primary Applicant.';

export const MANDATE_APPLICANT_DUPLICATE_PERSON_ERROR =
    'This person is already added as another applicant — each applicant must be a different person.';

export const REQUIRED_FIELD_ERROR = 'Required';
export const MANDATE_APPLICANT_INVALID_PAN_ERROR = 'Enter a valid PAN (e.g. ABCDE1234F)';

// Designation values the save payload's people[] sends to the backend - distinct from the
// card-header badge copy (MANDATE_APPLICANT_DESIGNATION_LABELS above), which is UI-only.
export const MANDATE_APPLICANT_DESIGNATION = {
    APPLICANT: 'Applicant',
    CO_APPLICANT: 'Co-Applicant',
} as const;

// Business Details field keys - every value collects into a single `businessDetails` object saved
// to Deal.customFields.businessDetails.
export const BUSINESS_DETAILS_FIELD_KEYS = {
    GSTIN: 'gstin',
    BUSINESS_ADDRESS: 'businessAddress',
    BUSINESS_ADDRESS_OWNERSHIP: 'businessAddressOwnership',
    BUSINESS_ADDRESS_PINCODE: 'businessAddressPincode',
    LOAN_PURPOSE: 'loanPurpose',
} as const;
